import { supabase } from '@/lib/supabase';
import {
  OPEN_FEEDBACK_STATUSES,
  type CreateFeedbackInput,
  type FeedbackQueueFilters,
  type FeedbackReport,
  type FeedbackStatus,
} from './types';

type DbRow = Record<string, unknown>;

const stringValue = (row: DbRow, key: string, fallback = '') =>
  typeof row[key] === 'string' ? (row[key] as string) : fallback;
const nullableString = (row: DbRow, key: string) => {
  const value = row[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

function mapReport(row: DbRow, emailById: Map<string, string>): FeedbackReport {
  const reporterId = stringValue(row, 'reporter_id');
  return {
    id: stringValue(row, 'id'),
    kind: stringValue(row, 'kind') === 'feature' ? 'feature' : 'bug',
    title: stringValue(row, 'title'),
    body: stringValue(row, 'body'),
    severity: nullableString(row, 'severity') as FeedbackReport['severity'],
    status: stringValue(row, 'status', 'new') as FeedbackStatus,
    reporterId,
    reporterEmail: emailById.get(reporterId) ?? null,
    route: nullableString(row, 'route'),
    appVersion: nullableString(row, 'app_version'),
    userAgent: nullableString(row, 'user_agent'),
    triagedBy: nullableString(row, 'triaged_by'),
    triageNote: nullableString(row, 'triage_note'),
    triagedAt: nullableString(row, 'triaged_at'),
    createdAt: stringValue(row, 'created_at'),
    updatedAt: stringValue(row, 'updated_at'),
  };
}

/**
 * Reports reference auth.users, not profiles, so PostgREST has no relationship
 * to embed. Resolving the handful of distinct reporters in a second query keeps
 * the schema uncoupled and costs one round trip.
 */
async function resolveReporterEmails(rows: DbRow[]): Promise<Map<string, string>> {
  const ids = Array.from(new Set(rows.map((row) => stringValue(row, 'reporter_id')).filter(Boolean)));
  if (ids.length === 0) return new Map();

  const { data, error } = await supabase.from('profiles').select('id,email').in('id', ids);
  if (error) return new Map();

  return new Map(
    ((data ?? []) as DbRow[])
      .map((row) => [stringValue(row, 'id'), stringValue(row, 'email')] as const)
      .filter(([id, email]) => Boolean(id) && Boolean(email)),
  );
}

/** Build id of the running app, so a report can be tied back to a deploy. */
async function currentAppVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const payload = (await response.json()) as { buildId?: string };
    return payload.buildId ?? null;
  } catch {
    return null;
  }
}

export async function canTriageFeedback(): Promise<boolean> {
  const { data, error } = await supabase.rpc('ringba_is_admin');
  if (error) return false;
  return data === true;
}

export async function createFeedbackReport(input: CreateFeedbackInput, route: string): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const reporterId = auth.user?.id;
  if (!reporterId) throw new Error('Sign in again to send this report.');

  const { error } = await supabase.from('feedback_reports').insert({
    kind: input.kind,
    title: input.title.trim(),
    body: input.body.trim(),
    severity: input.kind === 'bug' ? input.severity : null,
    reporter_id: reporterId,
    route: route.slice(0, 512),
    app_version: await currentAppVersion(),
    user_agent: navigator.userAgent.slice(0, 512),
  });

  if (error) throw new Error(`Could not send your report: ${error.message}`);
}

export async function getMyFeedbackReports(): Promise<FeedbackReport[]> {
  const { data: auth } = await supabase.auth.getUser();
  const reporterId = auth.user?.id;
  if (!reporterId) return [];

  // Filtered explicitly rather than leaning on RLS: an administrator can read
  // the whole queue, so "My reports" would otherwise show them everyone's.
  const { data, error } = await supabase
    .from('feedback_reports')
    .select('*')
    .eq('reporter_id', reporterId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(`Could not load your reports: ${error.message}`);

  const rows = (data ?? []) as DbRow[];
  return rows.map((row) => mapReport(row, new Map()));
}

export async function getFeedbackQueue(filters: FeedbackQueueFilters): Promise<FeedbackReport[]> {
  let query = supabase.from('feedback_reports').select('*');

  if (filters.status === 'open') query = query.in('status', OPEN_FEEDBACK_STATUSES);
  else if (filters.status !== 'all') query = query.eq('status', filters.status);

  if (filters.kind !== 'all') query = query.eq('kind', filters.kind);

  const { data, error } = await query.order('created_at', { ascending: false }).limit(200);
  if (error) throw new Error(`Could not load the report queue: ${error.message}`);

  const rows = (data ?? []) as DbRow[];
  const emailById = await resolveReporterEmails(rows);
  return rows.map((row) => mapReport(row, emailById));
}

export async function updateFeedbackStatus(
  reportId: string,
  patch: { status: FeedbackStatus; triageNote?: string | null },
): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('feedback_reports')
    .update({
      status: patch.status,
      triage_note: patch.triageNote ?? null,
      triaged_by: auth.user?.id ?? null,
      triaged_at: new Date().toISOString(),
    })
    .eq('id', reportId);

  if (error) throw new Error(`Could not update this report: ${error.message}`);
}
